// 全站錯誤邊界 (2026-07-24 使用者回報「換頁常常白畫面、要按重新整理」)。
//
// 之前完全沒有 ErrorBoundary → 任何 render / lazy-import 錯誤都會讓整個 React
// 樹 unmount = 白畫面。 這支:
//   · chunk 載入失敗 (舊分頁踩到新部署) → 自動重整一次自救 (見 chunk-reload.ts)。
//   · 其他錯誤 → 顯示友善畫面 + 重新整理鈕, 而不是一片白。
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError, reloadOnceForStaleChunk } from '../lib/chunk-reload.ts';

interface State {
  error: Error | null;
  /** chunk 錯誤但**沒有**觸發重整（剛重整過）→ 重整救不了，要給使用者看得懂的畫面。 */
  reloadTried?: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // 舊分頁 × 新部署 → 自動重整一次抓新 bundle。
      // 沒重整成功（10 秒內剛重整過）就記下來，render 要改顯示錯誤畫面而不是留白。
      this.setState({ reloadTried: !reloadOnceForStaleChunk() });
      return;
    }
    console.error('[ErrorBoundary]', error);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // chunk 錯誤且**真的**觸發了重整 → 給空白過場, 別閃一下錯誤 UI。
    // 但如果沒觸發（10 秒內剛重整過 = 重整救不了它），留白就是最糟的畫面 ——
    // 使用者只看到全白、不知道發生什麼事、也不知道能做什麼。 改顯示錯誤畫面。
    if (isChunkLoadError(error) && !this.state.reloadTried) return <div className="min-h-screen" />;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md text-center space-y-3">
          <div className="text-3xl">😵</div>
          <h1 className="text-lg font-semibold text-slate-800">頁面載入時發生問題</h1>
          <p className="text-sm text-slate-600">
            可能是系統剛更新。請重新整理頁面；若持續發生請通知 IT。
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm bg-slate-900 text-white rounded hover:bg-slate-800"
          >
            重新整理
          </button>
          {/* 2026-08-10: 原本這裡什麼都不顯示 → 使用者回報「還是會出現」時, 我們手上
              沒有任何線索, 只能一頁一頁猜。 把真正的錯誤訊息收在 <details> 裡:
              預設收合不嚇人, 但要追的時候一展開就有。 */}
          <details className="text-left pt-2">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
              技術細節
            </summary>
            <pre className="mt-2 text-[11px] text-slate-500 whitespace-pre-wrap break-all bg-slate-100 rounded p-2">
              {error.name}: {error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
